import React, { Fragment, HTMLProps } from 'react'

import ChevronUp from 'mdi-material-ui/ChevronUp'
import ChevronDown from 'mdi-material-ui/ChevronDown'
import ChevronRight from 'mdi-material-ui/ChevronRight'
import UnfoldMoreHorizontal from 'mdi-material-ui/UnfoldMoreHorizontal'

import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  ColumnDef,
  Column,
  ColumnOrderState,
  flexRender,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  Row,
  Header,
  Table,
  PaginationState,
  RowSelectionState,
  ColumnResizeMode,
  ColumnPinningState
} from '@tanstack/react-table'

import { DndProvider, useDrag, useDrop } from 'react-dnd/dist/index'
import { HTML5Backend } from 'react-dnd-html5-backend/dist/index'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import { Variation } from '@/@core/services/app.service'
import {
  ChevronLeft,
  ChevronDoubleLeft,
  ChevronDoubleRight,
  MenuDown,
  MenuUp,
  MenuSwap
} from 'mdi-material-ui'
import Select from '@mui/material/Select'
import TableDetails from './table-details/TableDetails'
import { isWindow, switchACMG } from '@/@core/utils/utils'
import { useVirtualizer } from '@tanstack/react-virtual'

type TableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData>[]
  renderSubComponent: (props: { row: Row<TData> }) => React.ReactElement
  getRowCanExpand: (row: Row<TData>) => boolean
  sorting: SortingState
  pagination: PaginationState
  rowSelection: RowSelectionState
  columnOrder: ColumnOrderState
  pageCount: number
  columnPinning: ColumnPinningState
  columnResizeMode: ColumnResizeMode
  setCellValue: any
  isFetching: boolean
  setPage: any
  variantCount: number
  totalCount: number
  analysisInfo: any
  isColorToggle: boolean
  setColumnPinning: React.Dispatch<React.SetStateAction<ColumnPinningState>>
  setColumnOrder: React.Dispatch<React.SetStateAction<ColumnOrderState>>
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  reorderRow: (draggedRowIndex: number, targetRowIndex: number) => void
}

interface Props {
  columns: ColumnDef<Variation | any>[]
  data: Variation[] | any[]
  sorting: SortingState
  pagination: PaginationState
  pageCount: number
  setRowSelection?: any
  setPagination?: any
  setCellValue?: any
  setSortQuery?: any
  keys?: any
  setBackop?: any
  isColorToggle?: boolean
  isFetching: boolean
  setPage?: any
  variantCount: number
  totalCount: number
  disabledExpand: boolean
  analysisInfo?: any
}

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(254,130,138)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgb(254,196,210)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(134,169,209)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(103,142,197)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

function Tables({
  data,
  columns,
  renderSubComponent,
  getRowCanExpand,
  sorting,
  setSorting,
  reorderRow,
  pagination,
  setPagination,
  rowSelection,
  setRowSelection,
  columnOrder,
  setColumnOrder,
  pageCount,
  columnResizeMode,
  setCellValue,
  columnPinning,
  setColumnPinning,
  isFetching,
  setPage,
  variantCount,
  totalCount,
  analysisInfo,
  isColorToggle
}: TableProps<Variation>): JSX.Element {
  const table = useReactTable<Variation>({
    data,
    columns,
    state: {
      sorting,
      pagination,
      rowSelection,
      columnOrder,
      columnPinning
    },
    onColumnPinningChange: setColumnPinning,
    columnResizeMode,
    pageCount: pageCount,
    onSortingChange: (sort: any) => {
      const sorting = sort()

      if (sorting[0].id === 'f_score' && !sorting[0].desc) {
        setSorting([])

        return
      } else if (sorting[0].id === 'g_score' && !sorting[0].desc) {
        setSorting([])

        return
      } else if (sorting[0].id === 'SpliceAI_max_score' && !sorting[0].desc) {
        setSorting([])

        return
      } else if (sorting[0].id === 'Revel_score' && !sorting[0].desc) {
        setSorting([])

        return
      } else if (sorting[0].id === 'pathogenicity' && !sorting[0].desc) {
        setSorting([])

        return
      }

      // else if (sorting[0].id === 'ACMG_priority' && sorting[0].desc) {
      //   setSorting([
      //     {
      //       desc: false,
      //       id: 'ACMG_priority'
      //     }
      //   ])

      //   return
      // }

      return setSorting(sort)
    },
    onPaginationChange: setPagination,
    getRowCanExpand,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: true,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    manualSorting: true,
    manualPagination: true,
    sortingFns: {
      myCustomSorting: (): number => 0
    }
  })

  // 虚拟化配置

  const rowVirtualizer = useVirtualizer({
    count: data.length,

    estimateSize: () => 50,

    overscan: 5,

    getScrollElement: () => tableConRef.current
  })

  const rows = table.getRowModel().rows

  const virtualRows = rowVirtualizer.getVirtualItems()
  const { t } = useTranslation()
  const tableRef = React.useRef<HTMLTableElement>(null)
  const tableConRef = React.useRef<HTMLDivElement>(null)

  const [tableMaxHeight, setTableMaxHeight] = React.useState<string>('75vh')

  React.useEffect(() => {
    //
  }, [tableMaxHeight])

  const getTableMaxHight = () => {
    const backdrop = document.getElementById('backdrop') as HTMLElement
    const header1 = document.getElementsByTagName('header')[0]
    const header2 = document.getElementById('header2') as HTMLElement
    const header3 = document.getElementById('header3') as HTMLElement
    const header4 = document.getElementsByTagName('main')[0]

    if (totalCount) {
      setTableMaxHeight(
        `${Math.floor(
          backdrop?.scrollHeight -
            header1?.scrollHeight -
            header2?.scrollHeight -
            header3?.scrollHeight -
            80
        )}px`
      )
    } else setTableMaxHeight('500px')

    return `${Math.floor(
      (1 -
        header1?.scrollHeight / window.screen.height -
        header2?.scrollHeight / window.screen.height -
        header3?.scrollHeight / window.screen.height) *
        100
    )}vh`
  }

  React.useEffect(() => {
    const tableCon = tableConRef.current as HTMLDivElement

    if (tableCon.scrollLeft) {
      handleScroll()
    }
  })

  React.useEffect(() => {
    getTableMaxHight()
  })

  const throttle = (fn: () => void, delay: number | undefined) => {
    let timer = null as any

    return function () {
      clearTimeout(timer)

      timer = setTimeout(() => {
        fn()
      }, delay)
    }
  }

  React.useEffect(() => {
    let lastPixelRatio = window.devicePixelRatio

    window.addEventListener('resize', function () {
      const currentPixelRatio = window.devicePixelRatio

      if (currentPixelRatio !== lastPixelRatio) {
        throttle(getTableMaxHight, 500)
      }

      lastPixelRatio = currentPixelRatio
    })
  })

  const handleScroll = () => {
    const tables = tableRef.current as HTMLTableElement
    const tableCon = tableConRef.current as HTMLDivElement
    const firstColumn = tables.querySelectorAll(
      'td:nth-child(1), th:nth-child(1)'
    ) as unknown as HTMLElement[]
    const secondColumn = tables.querySelectorAll(
      'td:nth-child(2), th:nth-child(2)'
    ) as unknown as HTMLElement[]
    const thirdColumn = tables.querySelectorAll(
      'td:nth-child(3), th:nth-child(3)'
    ) as unknown as HTMLElement[]
    const fourthColumn = tables.querySelectorAll(
      'td:nth-child(4), th:nth-child(4)'
    ) as unknown as HTMLElement[]
    const fiveColumn = tables.querySelectorAll(
      'td:nth-child(5), th:nth-child(5)'
    ) as unknown as HTMLElement[]
    const sixColumn = tables.querySelectorAll(
      'td:nth-child(6), th:nth-child(6)'
    ) as unknown as HTMLElement[]

    const handleScroll = () => {
      const scrollLeft = tableCon.scrollLeft
      const scrollTop = tableCon.scrollTop
      const clientHeight = tableCon.clientHeight
      const scrollHeight = tableCon.scrollHeight

      const totalDBRowCount = table.getPageCount()
      const totalFetched = data.length

      //

      if (
        scrollHeight - scrollTop - clientHeight < 800 &&
        !isFetching &&
        totalFetched < totalDBRowCount
      ) {
        setPage()
      }

      if (isWindow()) {
        firstColumn.forEach((element) => {
          element.style.left = `${0}px`

          element.style.position = 'sticky'

          element.style.zIndex = '1'

          // element.style.backgroundColor = 'rgb(240,239,240)'

          // element.style.color = 'inherit'

          if (scrollLeft === 0 || scrollLeft < 20) {
            // element.style.backgroundColor = 'initial'
            element.style.position = 'static'
          }
        })

        secondColumn.forEach((element) => {
          element.style.left = `${firstColumn[0].clientWidth}px`

          element.style.position = 'sticky'

          // element.style.color = 'inherit'

          // element.style.backgroundColor = 'rgb(240,239,240)'

          element.style.zIndex = '1'

          if (scrollLeft === 0 || scrollLeft < 20) {
            // element.style.backgroundColor = 'initial'
            element.style.position = 'static'
          }
        })

        thirdColumn.forEach((element) => {
          element.style.left = `${
            firstColumn[0].clientWidth + secondColumn[0].clientWidth
          }px`

          element.style.position = 'sticky'

          // element.style.backgroundColor = '#f5f5f5'

          // element.style.color = 'black'

          element.style.zIndex = '1'

          if (scrollLeft === 0 || scrollLeft < 20) {
            // element.style.backgroundColor = 'initial'
            // element.style.color = 'inherit'
            element.style.position = 'static'
          }
        })

        fourthColumn.forEach((element) => {
          element.style.left = `${
            firstColumn[0].clientWidth +
            secondColumn[0].clientWidth +
            thirdColumn[0].clientWidth
          }px`

          element.style.position = 'sticky'

          // element.style.backgroundColor = '#f5f5f5'

          element.style.zIndex = '1'

          if (scrollLeft === 0 || scrollLeft < 20) {
            // element.style.backgroundColor = 'initial'
            element.style.position = 'static'
          }
        })
      }
    }

    handleScroll()
  }

  return (
    <div
      className="p-2"
      style={{
        width: '100%'
      }}
    >
      <div className="h-2" />

      <div
        style={{
          width: '100%',
          height: tableMaxHeight,
          overflow: 'auto'
        }}
        ref={tableConRef}
        onScroll={(e) => {
          e.stopPropagation()

          handleScroll()
        }}
      >
        <table
          style={{
            border: '1px solid lightgray',

            minWidth: '100%',

            width: 'max-content'

            // width: table.getCenterTotalSize()
          }}
          ref={tableRef}
        >
          <thead
            style={{
              // borderBottom: '1px solid white',
              transform: 'scaleY (0.5)',
              backgroundColor: '#32B0C9',
              color: 'white',
              position: 'sticky',
              top: -1,
              zIndex: 2
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <th
                  style={{
                    // position: 'sticky',
                    zIndex: 1,
                    left: 0,
                    backgroundColor: '#32B0C9'
                  }}
                >
                  <IndeterminateCheckbox
                    {...{
                      checked: table.getIsAllRowsSelected(),
                      indeterminate: table.getIsSomeRowsSelected(),
                      onChange: table.getToggleAllRowsSelectedHandler()
                    }}
                  />
                </th>

                {/* <th
                  style={{
                    // borderBottom: '1px solid white',
                    transform: 'scaleY (0.5)',
                    padding: '2px 4px',
                    zIndex: 1,
                    left: 0,
                    backgroundColor: '#32B0C9'
                  }}
                /> */}

                {headerGroup.headers.map((header) => (
                  <DraggableColumnHeader
                    key={header.id}
                    header={header}
                    table={table}
                  />
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              return (
                <Fragment key={`F${row.id}`}>
                  <DraggableRow
                    key={row.id}
                    row={row}
                    reorderRow={reorderRow}
                    setCellValue={setCellValue}
                    index={index}
                    isColorToggle={isColorToggle}
                  />

                  {row.getIsExpanded() && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        style={{
                          // position: 'sticky',
                          left: 0,
                          zIndex: 1
                        }}
                      >
                        <TableDetails
                          params={row.original}
                          analysisInfo={analysisInfo}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="h-2" />

      <Grid
        container
        className="flex items-center gap-2"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1
        }}
      >
        <Grid item xs>
          <span
            className="flex items-center gap-1"
            style={{ alignItems: 'center', fontSize: '1rem', fontWeight: 600 }}
          >
            {`${t('Display')}${data.length}${
              totalCount
                ? `，${t('a total of')} ${totalCount} ${t(
                    'records'
                  )} - ${variantCount} ${t('variance')} - ${
                    data.length === totalCount
                      ? t('Data loading completed')
                      : !isFetching
                      ? t('Slide loading more')
                      : t('Please wait for a moment')
                  }`
                : ''
            }`}
          </span>
        </Grid>
      </Grid>
    </div>
  )
}

const renderSubComponent = ({ row }: { row: Row<Variation> }) => {
  return (
    <pre style={{ fontSize: '10px' }}>
      <code>{JSON.stringify(row.original, null, 2)}</code>
    </pre>
  )
}

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = React.useRef<HTMLInputElement>(null!)

  React.useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + ' cursor-pointer'}
      {...rest}
      style={{
        marginLeft: '5px',
        marginRight: '12px'
      }}
    />
  )
}

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  )

  return [...columnOrder]
}

const DraggableColumnHeader: React.FC<{
  header: Header<Variation, unknown>
  table: Table<Variation>
}> = ({ header, table }) => {
  const { getState, setColumnOrder } = table
  const { columnOrder } = getState()
  const { column } = header

  const [, dropRef] = useDrop({
    accept: 'column',
    drop: (draggedColumn: Column<Variation>) => {
      const newColumnOrder = reorderColumn(
        draggedColumn.id,
        column.id,
        columnOrder
      )

      setColumnOrder(newColumnOrder)
    }
  })

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    item: () => column,
    type: 'column'
  })

  return (
    <th
      ref={header.id === 'gene' ? null : dropRef}
      colSpan={header.colSpan}
      style={{
        opacity: isDragging ? 0.5 : 1,

        // borderBottom: '1px solid white',
        transform: 'scaleY (0.5)',
        color: 'white',
        padding: '10px 5px',
        position: header.id === 'gene' && isWindow() ? 'sticky' : 'static',
        zIndex: 1,
        left: 0,
        backgroundColor: '#32B0C9'
      }}
    >
      <div
        ref={previewRef}
        {...{
          className: header.column.getCanSort()
            ? 'cursor-pointer select-none th_border'
            : 'th_border',
          onClick: header.column.getToggleSortingHandler()
        }}
        style={{ padding: '0 5px', lineHeight: '2rem', position: 'relative' }}
      >
        <div
          ref={header.id === 'gene' ? null : dragRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            color: 'inherit',
            fontSize: '0.875rem'
          }}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}

          {header.column.getCanSort()
            ? {
                desc: <MenuDown />,
                asc: <MenuUp />
              }[header.column.getIsSorted() as string] ?? <MenuSwap />
            : ''}
        </div>
      </div>

      {/* <div
        {...{
          onMouseDown: header.getResizeHandler(),
          onTouchStart: header.getResizeHandler(),
          className: `resizer ${
            header.column.getIsResizing() ? 'isResizing' : ''
          }`,
          style: {
            transform: ''
          }
        }}
      /> */}
    </th>
  )
}

const DraggableRow: React.FC<{
  index: number
  row: Row<Variation>
  setCellValue: any
  isColorToggle: boolean
  reorderRow: (draggedRowIndex: number, targetRowIndex: number) => void
}> = ({ row, reorderRow, setCellValue, index, isColorToggle }) => {
  const [, dropRef] = useDrop({
    accept: 'row',
    drop: (draggedRow: Row<Variation>) =>
      reorderRow(draggedRow.index, row.index)
  })
  const { t } = useTranslation()

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    item: () => row,
    type: 'row'
  })

  let bgcolor = color.find(
    (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
  )
    ? (color.find((i) => i.key === row.original.CLINSIG_final?.toLowerCase())
        ?.color as any)
    : 'white'

  // loss_of_function等于1 并且CLINSIG_final不等于P/LP
  if (row.original.CLINSIG_final) {
    if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
      //
    } else if (
      row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
    ) {
      //
    } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'
  } else if (
    row.original.clinvar_clinsig_final ||
    row.original.gls_classification
  ) {
    if (
      row.original.clinvar_clinsig_final?.includes('PAT') ||
      row.original.clinvar_clinsig_final?.includes('LP')
    ) {
      if (row.original.gls_classification) {
        bgcolor = color.find(
          (i) => i.key === switchACMG(row.original.gls_classification)
        )
          ? (color.find(
              (i) => i.key === switchACMG(row.original.gls_classification)
            )?.color as any)
          : 'white'
      }
    } else {
      if (row.original.gls_classification) {
        bgcolor = color.find(
          (i) => i.key === switchACMG(row.original.gls_classification)
        )
          ? (color.find(
              (i) => i.key === switchACMG(row.original.gls_classification)
            )?.color as any)
          : 'white'
      }
      if (row.original.is_loss_of_function) bgcolor = '#f5d18c'
    }
  } else if (row.original.ccnv_classification) {
    bgcolor = color.find(
      (i) => i.key === row.original.ccnv_classification?.toLowerCase()
    )
      ? (color.find(
          (i) => i.key === row.original.ccnv_classification?.toLowerCase()
        )?.color as any)
      : 'white'
  }

  bgcolor = isColorToggle ? 'white' : bgcolor

  return (
    <tr
      ref={previewRef} //previewRef could go here
      style={{
        opacity: isDragging ? 0.5 : 1,

        // borderBottom: '1px solid white',
        backgroundColor: bgcolor,
        color:
          bgcolor === 'rgb(240,239,240)' ||
          bgcolor === '#f5d18c' ||
          bgcolor === 'rgb(245,182,193)' ||
          bgcolor === 'white'
            ? 'black'
            : 'white'
      }}
      onClick={(e) => {
        setCellValue && setCellValue(row)
      }}
      className={'tr'}
      key={`tb-tr${row.id}`}
    >
      <td
        style={{
          // position: 'sticky',
          zIndex: 1,
          backgroundColor: bgcolor,
          left: 0,
          padding: '5px 0',
          textAlign: 'center'
        }}
      >
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler()
          }}
        />
      </td>

      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          style={{
            // borderRight: '1px solid lightgray',

            // width: cell.column.getSize(),
            position: 'static',
            zIndex: 1,
            left: 0,
            backgroundColor: bgcolor,
            color:
              bgcolor === 'rgb(240,239,240)' ||
              bgcolor === '#f5d18c' ||
              bgcolor === 'rgb(245,182,193)' ||
              bgcolor === 'white' ||
              bgcolor === 'rgb(254,196,210)'
                ? 'black'
                : 'white'
          }}
          className="td"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  )
}

const TanStackTable = React.forwardRef((props: Props, ref) => {
  const { t } = useTranslation()

  const loaclColumnOrder: string[] = JSON.parse(
    localStorage.getItem('columnOrder') || '[]'
  )
  const columnOrders: string[] = props.columns.map(
    (column) => column.id as string
  )
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columns, setColumns] = React.useState<any[]>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    columns.map((column) => column.id as string)
  )
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>({
    left: ['grading', 'gene']
  })
  const [columnResizeMode, setColumnResizeMode] =
    React.useState<ColumnResizeMode>('onChange')
  const dataMemo = React.useMemo(() => props.data, [props.data])

  const reorderRow = (draggedRowIndex: number, targetRowIndex: number) => {
    dataMemo.splice(
      targetRowIndex,
      0,
      dataMemo.splice(draggedRowIndex, 1)[0] as Variation
    )
  }

  const resetRowSelect = () => {
    setRowSelection({})
  }

  //自定义排序
  React.useEffect(() => {
    if (sorting.length) {
      props.setSortQuery &&
        props.setSortQuery({
          [sorting[0].id]: !sorting[0].desc ? 1 : -1
        })
    } else props.setSortQuery && props.setSortQuery({ f: -1 })
  }, [sorting])

  React.useEffect(() => {
    setColumns(props.columns)
  }, [props.columns])

  //自定义排序
  React.useEffect(() => {
    //

    localStorage.setItem('columnOrder', JSON.stringify(columnOrder))
  }, [columnOrder])

  React.useEffect(() => {
    const arr = Object.keys(rowSelection).map((i) => Number(i))

    const keyIds = arr.map((i) => dataMemo[i].keyId)

    //

    props.setRowSelection && props.setRowSelection(arr, keyIds)
  }, [rowSelection])

  React.useImperativeHandle(ref, () => {
    return {
      resetRowSelect,
      dataMemo,
      rowSelection
    }
  })

  return (
    <DndProvider backend={HTML5Backend}>
      <Tables
        data={dataMemo}
        columns={columns}
        getRowCanExpand={() => !props.disabledExpand}
        renderSubComponent={renderSubComponent}
        sorting={sorting}
        setSorting={setSorting}
        pagination={props.pagination}
        setPagination={props.setPagination}
        reorderRow={reorderRow}
        setRowSelection={setRowSelection}
        setCellValue={props.setCellValue}
        rowSelection={rowSelection}
        setColumnOrder={setColumnOrder}
        columnOrder={columnOrder}
        pageCount={props.totalCount}
        columnResizeMode={columnResizeMode}
        columnPinning={columnPinning}
        setColumnPinning={setColumnPinning}
        isFetching={props.isFetching}
        setPage={props.setPage}
        variantCount={props.variantCount}
        totalCount={props.totalCount}
        analysisInfo={props.analysisInfo}
        isColorToggle={Boolean(props.isColorToggle)}
      />
    </DndProvider>
  )
})

export default TanStackTable
